// Tenancy scope options (BD-565/629).
//
// The reports-b Tenancy filter is a scope dimension exactly like
// Department / Location: a multi-select whose ticked values narrow the
// dashboard to a set of CHILD tenancies. Unlike the other dimensions its
// values are numeric tenancy ids (the backend accepts scope.tenancy as
// number[]), so the ids have to stay Numbers end to end.
//
// No new API: the tenancy list endpoint already returns every tenancy the
// caller can see (each carrying parentTenancyId), so the current tenancy's
// descendant subtree is derived client-side with buildTenancyTree - the same
// walk the tenancy Family Tree page uses.
//
// Integration hook: the descendant option list is reused by the shell filter
// chip (index.js) and the export builder, so both derive from one place.
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTenanciesQuery } from 'store/services/tenancy.service';
import { buildTenancyTree } from '../tenancy/tenancyTree';

// Matches the family-tree page: the endpoint pages by tenancy id, so the page
// has to be big enough to reach the descendants being listed.
const TENANCY_PAGE_SIZE = 5000;

export const MAX_DEFAULT_POOLED_DESCENDANTS = 200;

export function defaultTenancyIds(tenancyOptions, currentTenancyId) {
    if (!tenancyOptions.length) return [];
    if (tenancyOptions.length - 1 > MAX_DEFAULT_POOLED_DESCENDANTS) return [currentTenancyId];
    return tenancyOptions.map((o) => o.value);
}

// Whether the shell is still waiting to add the default tenancy filter for a first
// visit (nothing saved), so the pages can hold off rather than query without it
// and then again with it (BD-1112). `seededFor` is the tenancy last seeded or
// restored. A scoped user never gets the filter, but whether the user is scoped
// isn't known until their record loads; anyone else waits for the tenancy list,
// and then for the seed itself whenever there is a default to add.
export function defaultTenancyPending({ seededFor, tenancyId, scopedUserPending, isScopedUser, optionsLoaded, defaultFilters }) {
    if (seededFor === tenancyId) return false;
    if (scopedUserPending) return true;
    if (isScopedUser) return false;
    return !optionsLoaded || defaultFilters.length > 0;
}

export function descendantTenancyOptions(tenancies, currentTenancyId) {
    const currentId = parseInt(currentTenancyId, 10);
    const { tenancyMap } = buildTenancyTree(tenancies, currentId);
    const nodes = [...tenancyMap.values()];
    const descendants = nodes
        .filter((node) => node.id !== currentId)
        .map((node) => ({ label: node.name, value: node.id }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    // Leaf tenancy: no children => no tenancy filter at all.
    if (!descendants.length) return [];
    // The viewed tenancy itself heads the list so it can be isolated. Leaving the
    // filter EMPTY means "all tenancies" (self + descendants), which the BFF resolves
    // server-side - matching how the other dimension filters treat empty as "all".
    const self = nodes.find((node) => node.id === currentId);
    const selfOption = { label: self ? `${self.name} (this tenancy)` : 'This tenancy', value: currentId };
    return [selfOption, ...descendants];
}

// Hook wrapper: fetches the tenancy list (cached by RTK Query) and returns the
// descendant options for the active tenancy. Empty array => no child tenancies
// => callers should not render the tenancy chip. `loaded` tells that apart from
// a list that hasn't arrived yet; it is also true once the request has failed.
export function useDescendantTenancyState() {
    const currentTenancyId = useSelector((state) => state.tenancy.tenancyId);
    const { data: tenancies, isSuccess, isError } = useGetTenanciesQuery({
        perPage: TENANCY_PAGE_SIZE,
        page: 0,
        includes: '',
        searchTerm: '',
        additionalQueryParams: '',
    });
    const options = useMemo(
        () => descendantTenancyOptions(tenancies, currentTenancyId),
        [tenancies, currentTenancyId],
    );
    return { options, loaded: isSuccess || isError };
}

export function useDescendantTenancyOptions() {
    return useDescendantTenancyState().options;
}
